import type { FC, ReactElement, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { GetRooftopFeaturesResponseDTO } from 'declarations/dto/features';
import type { FeaturesNamesE } from 'declarations/enums/features';

/**
 * Feature toggles for Chirp were previously loaded via `GET /rooftop-features/{id}` per rooftop.
 * That fan-out does not scale (e.g. hundreds of dealerships). Until the API exposes a single
 * user-scoped payload (e.g. on profile), this context stays inert: no network calls from the UI.
 */
export type FeaturesContextT = {
  rootTopFeatures: GetRooftopFeaturesResponseDTO | undefined;
  isFeatureActive: (featureName: FeaturesNamesE) => boolean;
};

type FeaturesProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  rootTopFeatures: undefined,
  isFeatureActive: (): boolean => false,
};

export const FeaturesContext = createContext<FeaturesContextT>(contextDefaultValue);

const FeaturesProvider: FC<FeaturesProviderPropsT> = ({ children }): ReactElement => {
  const isFeatureActive = (_featureName: FeaturesNamesE): boolean => false;

  return (
    <FeaturesContext.Provider
      value={{
        rootTopFeatures: undefined,
        isFeatureActive,
      }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};

const useFeatures = (): FeaturesContextT => useContext(FeaturesContext);

export { FeaturesProvider, useFeatures };
